<template>
  <div class="cost-calculator">
    <!-- 模式切换 -->
    <div class="mode-switch-bar">
      <button
        :class="['mode-btn', { active: currentMode === 'simple' }]"
        @click="switchMode('simple')"
      >简单分摊</button>
      <button
        :class="['mode-btn', { active: currentMode === 'discount' }]"
        @click="switchMode('discount')"
      >拼单满减</button>
    </div>

    <!-- 引导说明区域 -->
    <div class="guide-section">
      <h3 class="guide-title">使用指南</h3>
      <div class="guide-content">
        <template v-if="currentMode === 'simple'">
          <p class="guide-text">费用分摊计算器帮助您轻松解决各种费用分摊问题，支持以下场景：</p>
          <ul class="guide-list">
            <li>✅ 一人垫付，多人均摊</li>
            <li>✅ 多人垫付，自动计算</li>
            <li>✅ 复杂支付关系处理</li>
          </ul>
          <p class="guide-hint">💡 提示：填写参与人信息，系统将自动计算每个人应支付或应收取的金额</p>
        </template>
        <template v-else>
          <p class="guide-text">拼单满减计算器帮助您公平分摊购物优惠：</p>
          <ul class="guide-list">
            <li>✅ 每人录入商品及价格</li>
            <li>✅ 配置满减规则和优惠券</li>
            <li>✅ 按比例/人均/发起人优先分配优惠</li>
            <li>✅ 凑单建议提示</li>
          </ul>
          <p class="guide-hint">💡 提示：录入商品信息后系统自动计算总金额并最优分摊优惠</p>
        </template>
      </div>
    </div>

    <!-- 表单录入区域 -->
    <FormInputSection
      :initial-form="form"
      :mode="currentMode"
      :suggestion-data="suggestionData"
      @form-change="handleFormChange"
      @calculate="handleCalculate"
      @reset="handleReset"
    />

    <!-- 分摊结果区域 -->
    <ResultDisplay
      :result-list="resultList"
      :show-result="showResult"
      :mode="currentMode"
      :discount-summary="discountSummary"
    />

    <!-- 账单图片生成 -->
    <BillPreview
      :visible="showBillPreview"
      :result-list="resultList"
      :form="form"
      :discount-summary="discountSummary"
    />

    <!-- 操作按钮区域 -->
    <ActionButtons
      :has-result="showResult && resultList.length > 0"
      @reset="handleReset"
      @show-history="showHistoryModal"
      @save-record="handleSaveRecord"
      @copy-text="handleCopyText"
      @generate-bill-image="handleShowBillPreview"
      @export-excel="handleExportExcel"
      @export-txt="handleExportTXT"
    />

    <!-- 历史记录弹窗 -->
    <HistoryModal
      :visible="showHistory"
      :history-records="historyRecords"
      @close="closeHistoryModal"
      @delete="handleDeleteHistoryRecord"
    />
  </div>
</template>

<script>
import FormInputSection from './FormInputSection.vue';
import ResultDisplay from './ResultDisplay.vue';
import ActionButtons from './ActionButtons.vue';
import HistoryModal from './HistoryModal.vue';
import BillPreview from './BillPreview.vue';

import { calculateCost, validateForm, computeResult } from '../utils/calculator';
import { validateDiscountRules } from '../utils/discount-engine';
import { saveRecord as saveRecordToStorage, getHistoryRecords, deleteHistoryRecord } from '../utils/storage';
import { exportExcel, exportTXT, formatResultAsText } from '../utils/exporter';

export default {
  name: 'CostCalculator',
  components: {
    FormInputSection,
    ResultDisplay,
    ActionButtons,
    HistoryModal,
    BillPreview
  },
  data() {
    return {
      currentMode: 'simple',
      form: {
        totalAmount: 0,
        personList: [
          { name: '', payAmount: 0 },
          { name: '', payAmount: 0 }
        ],
        proxyList: [
          { proxyName: '', beProxyName: '', proxyAmount: 0 }
        ],
        discountRules: {
          enabled: false,
          thresholds: [{ threshold: 0, discount: 0 }],
          coupons: [{ name: '', amount: 0, threshold: 0, applicablePerson: '' }],
          strategy: 'proportional',
          organizer: ''
        }
      },
      resultList: [],
      discountSummary: null,
      suggestionData: null,
      showResult: false,
      showBillPreview: false,
      showHistory: false,
      historyRecords: []
    }
  },
  mounted() {
    this.historyRecords = getHistoryRecords();
  },
  methods: {
    switchMode(mode) {
      this.currentMode = mode
      this.handleReset()
    },

    handleFormChange(formData) {
      this.form = formData;
    },

    handleCalculate() {
      if (this.currentMode === 'discount' && this.form.discountRules && this.form.discountRules.enabled) {
        const validationResult = validateDiscountRules(this.form.discountRules);
        if (!validationResult.valid) {
          alert(validationResult.message);
          return;
        }
        // 校验参与人信息
        if (this.form.personList.length < 1) {
          alert('至少需要1个参与人！');
          return;
        }
        if (this.form.personList.some(item => !item.name.trim())) {
          alert('所有参与人姓名不能为空！');
          return;
        }
        const totalAmount = this.form.personList.reduce((sum, p) =>
          sum + (p.items || []).reduce((s, it) => s + (it.price || 0), 0), 0);
        if (totalAmount <= 0) {
          alert('总金额必须大于0！');
          return;
        }

        const result = computeResult(this.form, 'discount');
        this.resultList = result.resultList;
        this.discountSummary = result.discountSummary;
        this.suggestionData = result.suggestionData;
      } else {
        const validationResult = validateForm(this.form);
        if (!validationResult.valid) {
          alert(validationResult.message);
          return;
        }
        this.resultList = calculateCost(this.form);
        this.discountSummary = null;
        this.suggestionData = null;
      }
      this.showResult = true;
    },

    handleReset() {
      this.form = {
        totalAmount: 0,
        personList: this.currentMode === 'discount'
          ? [{ name: '', payAmount: 0, items: [{ itemName: '', price: 0 }] }, { name: '', payAmount: 0, items: [{ itemName: '', price: 0 }] }]
          : [{ name: '', payAmount: 0 }, { name: '', payAmount: 0 }],
        proxyList: [{ proxyName: '', beProxyName: '', proxyAmount: 0 }],
        discountRules: {
          enabled: false,
          thresholds: [{ threshold: 0, discount: 0 }],
          coupons: [{ name: '', amount: 0, threshold: 0, applicablePerson: '' }],
          strategy: 'proportional',
          organizer: ''
        }
      };
      this.resultList = [];
      this.discountSummary = null;
      this.suggestionData = null;
      this.showResult = false;
    },

    handleShowBillPreview() {
      this.showBillPreview = !this.showBillPreview;
    },

    handleSaveRecord() {
      saveRecordToStorage(this.form, this.resultList);
      this.historyRecords = getHistoryRecords();
      alert('保存成功！');
    },

    handleCopyText() {
      const text = formatResultAsText(this.resultList, this.form, this.discountSummary, this.currentMode);
      navigator.clipboard.writeText(text).then(() => {
        alert('结算信息已复制到剪贴板');
      }).catch(() => {
        alert('复制失败，请手动复制');
      });
    },

    showHistoryModal() {
      this.showHistory = true;
    },

    closeHistoryModal() {
      this.showHistory = false;
    },

    handleDeleteHistoryRecord(id) {
      this.historyRecords = deleteHistoryRecord(id);
    },

    handleExportExcel() {
      exportExcel(this.resultList, this.form, this.discountSummary);
      alert('导出Excel成功！');
    },

    handleExportTXT() {
      exportTXT(this.resultList, this.form, this.discountSummary);
      alert('导出TXT成功！');
    }
  }
};
</script>

<style scoped>
.cost-calculator {
  max-width: 800px;
  margin: 0 auto;
  padding: 15px;
  font-family: "Microsoft YaHei", Arial, sans-serif;
}

.mode-switch-bar {
  display: flex;
  gap: 0;
  margin-bottom: 15px;
  border-radius: 8px;
  overflow: hidden;
}

.mode-btn {
  flex: 1;
  padding: 10px 16px;
  border: 2px solid #667eea;
  background: #fff;
  color: #667eea;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.mode-btn:first-child {
  border-radius: 8px 0 0 8px;
}

.mode-btn:last-child {
  border-radius: 0 8px 8px 0;
}

.mode-btn.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
}

.guide-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 10px;
  border-radius: 6px;
  margin-bottom: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.guide-title {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 8px;
  text-align: center;
}

.guide-content {
  line-height: 1.4;
}

.guide-text {
  margin-bottom: 6px;
  font-size: 13px;
}

.guide-list {
  margin: 6px 0;
  padding-left: 18px;
  font-size: 12px;
}

.guide-list li {
  margin-bottom: 3px;
}

.guide-hint {
  background: rgba(255, 255, 255, 0.2);
  padding: 6px;
  border-radius: 3px;
  margin-top: 8px;
  font-size: 11px;
  font-style: italic;
}

@media (max-width: 750px) {
  .cost-calculator {
    padding: 8px 0;
  }

  .mode-btn {
    font-size: 13px;
    padding: 8px 12px;
  }

  .guide-section {
    padding: 8px;
    margin-bottom: 12px;
    border-radius: 5px;
  }

  .guide-title {
    font-size: 13px;
    margin-bottom: 8px;
  }

  .guide-text, .guide-list, .guide-hint {
    font-size: 11px;
  }

  .guide-list {
    padding-left: 16px;
    margin: 5px 0;
  }

  .guide-hint {
    padding: 5px;
    margin-top: 6px;
    font-size: 10px;
  }
}

@media (max-width: 320px) {
  .mode-btn {
    font-size: 12px;
    padding: 6px 10px;
  }

  .guide-section {
    padding: 6px;
    margin-bottom: 10px;
  }

  .guide-text, .guide-list {
    font-size: 10px;
  }

  .guide-hint {
    font-size: 9px;
    padding: 4px;
  }

  .guide-list li {
    margin-bottom: 2px;
  }
}
</style>
